// councilExport.js — Council v2 export / reuse derivations (PURE).
//
// Two surfaces the SPEC requires: export a finished deliberation as Markdown
// (and copy it), and REUSE a prior deliberation by rebuilding the composer/
// config payload from a history detail. Both are pure so they can be tested
// without React and so the renderer stays UI-only.
//
// Reuse must reconstruct selected agents + chairman + mode truthfully; it must
// not fabricate a rebuttal round for a v1 row (mode null) or claim a cost when
// the run never reported one.

import { statusLabel } from '../../lib/councilProtocol.js';

function agentLabel(id) {
  return id || 'desconhecido';
}

/**
 * Build a stable Markdown export of a deliberation. Accepts the NORMALIZED
 * deliberation shape used by the hook / history detail. Unknown cost is stated
 * as unknown, never as $0.
 *
 * @param {object} d
 * @returns {string}
 */
export function deliberationToMarkdown(d = {}) {
  const lines = [];
  lines.push('# Conselho — deliberação');
  lines.push('');
  lines.push(`> ${d.query || '(sem pergunta)'}`);
  lines.push('');
  lines.push(`- Status: ${statusLabel(d.status)}`);
  if (d.mode) lines.push(`- Modo: ${d.mode === 'rebuttal' ? 'Rebuttal' : 'Essential'}`);
  lines.push(`- Conselheiros: ${(d.agents || []).map(agentLabel).join(', ') || '—'}`);
  if (d.effectiveChairman || d.chairman) {
    lines.push(`- Chairman efetivo: ${agentLabel(d.effectiveChairman || d.chairman)}`);
  }
  const saveNote = d.saved === false ? 'Não salva no histórico' : null;
  if (saveNote) lines.push(`- Persistência: ${saveNote}`);
  lines.push('');

  for (const phase of ['opinions', 'review', 'rebuttal', 'synthesis']) {
    const section = phaseSection(d, phase);
    if (!section) continue;
    lines.push(`## ${section.title}`);
    lines.push('');
    lines.push(section.body);
    lines.push('');
  }
  return lines.join('\n').trimEnd() + '\n';
}

function phaseSection(d, phase) {
  if (phase === 'opinions') {
    const opinions = d.opinionsByAgent || {};
    const ids = Object.keys(opinions);
    if (ids.length === 0) return null;
    const body = ids
      .map((id) => `### ${agentLabel(id)}\n\n${opinions[id] || ''}`)
      .join('\n\n');
    return { title: 'Opiniões', body };
  }
  if (phase === 'review') {
    const rankings = d.rankings || [];
    if (rankings.length === 0) return null;
    const body = rankings
      .map((r) => `### ${agentLabel(r.agent_id)}\n\n${r.ranking || (r.parsed_ranking || []).join(' > ') || ''}`)
      .join('\n\n');
    return { title: 'Peer review', body };
  }
  if (phase === 'rebuttal') {
    const rebuttals = d.rebuttalsByAgent || {};
    const ids = Object.keys(rebuttals);
    if (ids.length === 0) return null;
    const body = ids
      .map((id) => `### ${agentLabel(id)}\n\n${rebuttals[id] || ''}`)
      .join('\n\n');
    return { title: 'Réplicas', body };
  }
  if (phase === 'synthesis') {
    if (!d.synthesis) return null;
    const by = d.effectiveChairman || d.chairman;
    return { title: `Síntese${by ? ` (${agentLabel(by)})` : ''}`, body: d.synthesis };
  }
  return null;
}

/**
 * Derive the composer/config payload to REUSE a prior deliberation. Returns the
 * selection that should be restored, or null when the row is too incomplete to
 * reuse meaningfully (no agents).
 *
 * @param {object} detail  history detail (v1 or v2 shape)
 * @returns {{query: string, agents: string[], chairman: string, mode: 'essential'|'rebuttal'}|null}
 */
export function reusePayloadFromHistory(detail = {}) {
  const agents = Array.isArray(detail.agents) ? detail.agents.filter((a) => typeof a === 'string' && a) : [];
  if (agents.length === 0) return null;
  const chairman = typeof detail.chairman === 'string' && agents.includes(detail.chairman)
    ? detail.chairman
    : agents[0];
  // v1 rows (no mode) reuse as essential so we do not invent a rebuttal round.
  const mode = detail.mode === 'rebuttal' ? 'rebuttal' : 'essential';
  return {
    query: typeof detail.query === 'string' ? detail.query : '',
    agents,
    chairman,
    mode,
  };
}

/**
 * Normalize a persisted deliberation row (history detail) into the same shape
 * the live hook exposes, so the UI can render history with the exact same
 * phase components. v1 rows (stage1/stage2/stage3) map onto v2 fields.
 *
 * @param {object} row
 * @returns {object}
 */
export function normalizeHistoryDetail(row = {}) {
  const stage1 = arrayOrEmpty(row.stage1_results);
  const stage3 = row.stage3_result || {};
  return {
    ...historyMetadata(row, stage3),
    opinionsByAgent: byAgentMap(stage1),
    rankings: arrayOrEmpty(row.stage2_results),
    labelMapping: row.label_mapping || rebuildLabelMapping(stage1),
    rebuttalsByAgent: byAgentMap(arrayOrEmpty(row.stage_rebuttal)),
    synthesis: stage3.response || '',
  };
}

const historyMetadata = (row, stage3) => ({
  id: row.id || null, query: row.query || '', agents: arrayOrEmpty(row.agents),
  chairman: row.chairman || null,
  effectiveChairman: row.effective_chairman || stage3.agent_id || null,
  status: row.status || 'complete', mode: row.mode || null,
  protocolVersion: row.protocol_version || 1, saved: row.saved !== false,
  costKnown: row.cost_known === true, knownCostUsd: numberOr(row.known_cost_usd),
  totalCostUsd: numberOr(row.total_cost_usd), durationMs: numberOr(row.duration_ms),
  createdAt: row.created_at ?? null,
});

const arrayOrEmpty = (value) => Array.isArray(value) ? value : [];
const numberOr = (value) => typeof value === 'number' && Number.isFinite(value) ? value : null;

function byAgentMap(rows) {
  const map = {};
  for (const r of rows) if (r?.agent_id) map[r.agent_id] = r.response || '';
  return map;
}

function rebuildLabelMapping(stage1) {
  const mapping = {};
  stage1.forEach((row, index) => {
    if (row.agent_id) mapping[String.fromCharCode(65 + index)] = row.agent_id;
  });
  return mapping;
}

export function downloadCouncilMarkdown(markdown) {
  const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `conselho-${Date.now()}.md`;
  anchor.click();
  URL.revokeObjectURL(url);
}
